import { Fragment, useEffect, useState } from "react";

import { Classes, Text } from "@blueprintjs/core";

import execWm from "@/../../hypetrain/static/img/exec-wm.png";
import { Box, Flex } from "@/components/layout/flexbox";
import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { InlineEditableImage } from "@/components/pieces/InlineEditableImage";
import { InlineEditableText } from "@/components/pieces/InlineEditableText";
import { CustomLink } from "@/components/pieces/SmartLinkMarkdown";
import { BRAND_PURPLE, INNER_BORDER_COLOR } from "@/css/constants";
import { ParseLinkMetadata, useParseLinkMetadataMutation } from "@/graphql";

export const CARD_IMG_HEIGHT = 148;
const CARD_IMG_WIDTH = 150;

type RequestStatus = "idle" | "loading" | "success" | "error";

interface UrlData {
  text: string;
  description?: string | null;
  favicon?: string | null;
  imageSrc?: string | null;
  url: string;
}

export interface LinkPreviewMetadata {
  text: string;
  imageSrc?: string | null;
}

const extractTitleFromUrl = (url: string) => {
  const urlObject = new URL(url);

  const pathSegments = urlObject.pathname.split("/").filter(Boolean);
  const text =
    pathSegments.length > 0
      ? pathSegments[pathSegments.length - 1]
      : urlObject.hostname;

  return text
    .replace(/-/g, " ")
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase(),
    );
};

function useUnfurlUrl(
  url: string,
  metadata?: { text: string; imageSrc?: string | null } | null,
) {
  const [parseLinkMetadata] = useParseLinkMetadataMutation();
  const [status, setStatus] = useState<RequestStatus>("idle");
  const [data, setData] = useState<UrlData>();

  const handleUrl = async (url: string) => {
    setStatus("loading");

    if (isValidHttpOrHttpsUrl(url) === false) {
      setData({
        text: url,
        description: "",
        url,
      });
      setStatus("error");

      return;
    }

    const response = await parseLinkMetadata({
      variables: { link: url },
    });

    const parseLinkMetadataResponse: ParseLinkMetadata | undefined | null =
      response.data?.parseLinkMetadata;

    if (parseLinkMetadataResponse?.ok) {
      setData({
        text: parseLinkMetadataResponse.data?.text ?? "",
        imageSrc: parseLinkMetadataResponse.data?.imageSrc,
        url,
      });
      setStatus("success");
    } else {
      setData({
        text: extractTitleFromUrl(url),
        description: "",
        url,
      });
      setStatus("error");
    }
  };

  useEffect(() => {
    if (status === "idle" && metadata) {
      setStatus("success");
      setData({
        text: metadata.text,
        imageSrc: metadata.imageSrc,
        url,
      });
    } else {
      handleUrl(url);
    }
  }, [url]);

  useEffect(() => {
    if (metadata) {
      setData({
        text: metadata.text,
        imageSrc: metadata.imageSrc,
        url,
      });
    }
  }, [metadata]);

  return { status, data };
}

interface UnfurledUrlPreviewProps {
  url: string;
  urlData?: UrlData;
  isEditable: boolean;
  onSave?: (value: LinkPreviewMetadata) => void;
  metadata?: LinkPreviewMetadata | null;
}

function UnfurledUrlPreview({
  url,
  urlData,
  isEditable,
  onSave,
}: UnfurledUrlPreviewProps) {
  const { text, imageSrc: imgUrl } = urlData ?? {};
  const urlObject = isValidHttpOrHttpsUrl(url) ? new URL(url) : null;
  const Container = isEditable ? Fragment : CustomLink;
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isEditable && onSave && urlData) {
      onSave({
        text: urlData.text,
        imageSrc: urlData.imageSrc ?? undefined,
      });
    }
  }, []);

  return (
    <Container href={url}>
      <Flex
        className="xs:rounded-md xs:p-2.5 xs:gap-2.5 max-h-25 w-full flex-row bg-white md:gap-3 md:rounded-lg md:p-3"
        backgroundColor={"callout-background"}
        height={CARD_IMG_HEIGHT}
        onMouseEnter={() => {
          if (!isEditable) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (!isEditable) {
            setIsHovered(false);
          }
        }}
        sx={{
          gap: "16px",
          borderRadius: "8px",
          overflow: "hidden",
          transitionProperty: "background",
          transitionDuration: "fast",
          transitionTimingFunction: "ease-in-out",
          border: isHovered
            ? `1px solid ${BRAND_PURPLE}`
            : `1px solid ${INNER_BORDER_COLOR}`,
        }}
      >
        <Flex
          maxWidth={CARD_IMG_WIDTH}
          justifyContent="center"
          style={{
            borderRadius: "6px",
          }}
        >
          <InlineEditableImage
            containerStyle={{
              height: "100%",
              borderRadius: 6,
              position: "relative",
              alignItems: "center",
            }}
            allowMultipleUploads={false}
            endpoint={`${API_HOST}/files`}
            isEditable={isEditable}
            imageSrc={imgUrl || execWm}
            onUploadSuccess={(fileUrl?: string) => {
              onSave?.({ text: urlData?.text ?? "", imageSrc: fileUrl });
            }}
          />
        </Flex>

        <Flex flexDirection="column" justifyContent="flex-start" width="100%">
          {text && (
            <InlineEditableText
              className="xs:text-xs line-clamp-2 font-semibold text-gray-800 md:text-sm"
              value={text}
              isEditable={isEditable}
              onSave={(value) => {
                onSave?.({
                  text: value,
                  imageSrc: urlData?.imageSrc ?? undefined,
                });
              }}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
          )}

          {urlObject?.hostname && (
            <Box className="xs:text-xs font-normal text-gray-500 md:text-sm">
              {isEditable ? (
                <CustomLink href={url}>{urlObject.hostname}</CustomLink>
              ) : (
                <Text>{urlObject.hostname}</Text>
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    </Container>
  );
}

interface LinkPreviewProps {
  url: string;
  isEditable: boolean;
  onSave?: (value: LinkPreviewMetadata) => void;
  metadata?: LinkPreviewMetadata | null;
}

export function LinkPreview({
  url,
  isEditable,
  onSave,
  metadata,
}: LinkPreviewProps) {
  const { data, status } = useUnfurlUrl(url, metadata);

  if (status === "success" || status === "error") {
    return (
      <UnfurledUrlPreview
        url={url}
        urlData={data}
        isEditable={isEditable}
        onSave={onSave}
        metadata={metadata}
      />
    );
  }

  return (
    <Flex
      className="xs:rounded-md xs:p-2.5 xs:gap-2.5 bg-white md:gap-3 md:rounded-lg md:p-3"
      backgroundColor={"callout-background"}
      height={CARD_IMG_HEIGHT}
      width="100%"
      maxHeight="100px"
      flexDirection="row"
      style={{
        borderRadius: "8px",
        overflow: "hidden",
        transitionProperty: "background",
        transitionDuration: "fast",
        transitionTimingFunction: "ease-in-out",
      }}
    >
      <Box
        className={Classes.SKELETON}
        width="30%"
        style={{ borderRadius: 6 }}
      />

      <Flex flexDirection="column" p={3} style={{ gap: "8px" }} flex={1}>
        <Box
          className={Classes.SKELETON}
          height="30%"
          style={{ borderRadius: 6 }}
        />
        <Box
          className={Classes.SKELETON}
          height="30%"
          style={{ borderRadius: 6 }}
        />
      </Flex>
    </Flex>
  );
}
